"use strict";

import textObject from '../../texts';
import config from '../../configLoader';
import Session from '../../Models/Session';
import PageProcessor from '../PageProcessor';
import Price from '../../Helpers/Price';
import Utils from '../../Helpers/Utils';
import ModalHelper from "../../Helpers/ModalHelper";
import ObjectDataLoader from '../../Helpers/ObjectDataLoader';

export default class CheckoutPage extends PageProcessor {

	constructor()
	{
		super();
		this.loadedContent = null;
		this.displayType = 'checkout';
		this.collectedDataLoader = null;
		this.sessionKeys = {
			"status": "sparxpres-checkout-has-been-completed",
			"collectedData": "sparxpres-checkout-collected-data"
		};
	}

	/**
	 * Initialize processor
	 */
	initialize()
	{
		let self = this;
		self.debug("CheckoutPage.initialize...");
		self.bindElements(self.settings);

		if (!self.hasBeenInitialized) {
			self.hasBeenInitialized = true;

			// make sure price, reference and paymentMethod is set on load.
			let collectedData = Session.get(self.sessionKeys.collectedData) || {};
			if (self.elementBinding.getValue("reference")) collectedData.reference = self.elementBinding.getValue("reference");
			if (collectedData.paymentMethod) collectedData.paymentMethod = "";

			Session.set(self.sessionKeys.collectedData, collectedData);
			self.collectedDataLoader = new ObjectDataLoader(collectedData);

			self.onInitialization();
		}

		self.fetchPrice();
	}

	onInitialization()
	{
		let self = this;

		self.addEventListener('sparxpresDirtyElements', (event) => {
			let dirtyFields = event.detail.customData;
			let dataObject = {};

			for (let key in dirtyFields) {
				if ( /^(price|email|reference|phone|paymentMethod)$/.test(key) ) {
					self.debug("CheckoutPage.sparxpresDirtyElements event caught, key=%s", key);
					if (key === "price") {
						let _cleanedPrice = Price.cleanPrice(dirtyFields[key].newValue, self.isInDebugMode);
						self.debug("CheckoutPage.sparxpresDirtyElements -> price: %s, cleaned: %s, getPrice: %s", dirtyFields[key].newValue, _cleanedPrice, self.getPriceCents());
						if (_cleanedPrice > 0) {
//							dataObject[key] = _cleanedPrice;

							if (self.getPriceCents() !== _cleanedPrice) {
								self.priceLoaded(_cleanedPrice);
							}
						}
					} else {
						dataObject[key] = dirtyFields[key].newValue;
					}
				}
			}

			if (Object.keys(dataObject).length > 0) {
				// set/update checkout session values
				let sessionObject = Session.get(self.sessionKeys.collectedData) || {};
				Object.assign(sessionObject, dataObject);
				Session.set(self.sessionKeys.collectedData, sessionObject);
				self.collectedDataLoader.updateData(sessionObject);
			}
		});

		self.addEventListener('sparxpresLoanApplicationOpen', (event) => {
			self.debug("CheckoutPage.sparxpresLoanApplicationOpen event caught");
			ModalHelper.showLoanApplicationPage(self.getLoanApplicationUrl());
		});

		self.addElementRefreshEvents(self.settings);
		self.addPostMessageEventListener();

		self.appendCustomCssToHead(self.settings.display.customCSS);
	}

	/**
	 * Fetch price
	 */
	fetchPrice(retryCnt = 0, maxRetryCnt = 10) {
		let self = this;

		self.elementBinding.hardRefreshElement("price");
		let price = self.getPriceCents() || self.collectedDataLoader.get("price") || self.collectedDataLoader.get("cartPrice");
		if (price > 0) {
			self.priceLoaded(price);
		} else {
			if (retryCnt < maxRetryCnt) {
				const delaySeconds = retryCnt > 4 ? 2 : 1;
				self.debug("CheckoutPage, price not found, wait %d seconds before retrying, retry %d of %d", delaySeconds, retryCnt+1, maxRetryCnt);
				Utils.delay(delaySeconds*1000).then(() => {
					self.fetchPrice(retryCnt+1, maxRetryCnt);
				});
			} else {
				Utils.info("CheckoutPage, we didn't find the price.");
			}
		}
	}

	/**
	 * called when price is loaded
	 * @param priceCents
	 */
	priceLoaded(priceCents = 0) {
		let self = this;

		self.debug("CheckoutPage.priceLoaded start, price is %d", priceCents);

		self.cleanedPriceCents = priceCents;
		self.collectedDataLoader.price = priceCents;

		// set/update session price value
		let checkoutData = Session.get(self.sessionKeys.collectedData);
		checkoutData.price = priceCents;
		Session.set(self.sessionKeys.collectedData, checkoutData);

		self.getContent()
			.then(content => {
				content.html = self.wrapHTMLInDisplayContainer(content.html);
				self.loadedContent = content;

				self.elementBinding.hardRefreshElement("sparxpresContent", true, true);
				if (self.elementBinding.getElement("sparxpresContent")) {
					// sparxpresContent already in DOM, remove it
					document.querySelector("." + self.getWrapperClass()).remove();
				} else {
					// Replace checkout description text?
					if (self.settings.display.hasOwnProperty('paymentTextSelector')) {
						self.setPaymentTextDescription(self.settings.display.paymentTextSelector, content.title);
					}
				}

				self.attachContent(content.style, content.html)
					.then(result => {
						if (result) {
							self.contentAttached(content.script);
							self.runScript(self.settings.display.customJS);
						}
					});
			});
	}

	/**
	 * Replace checkout description text?
	 * @param paymentTextSelector
	 * @param title
	 * @param retryCnt
	 * @param maxRetryCnt
	 */
	setPaymentTextDescription(paymentTextSelector = '', title = '', retryCnt = 0, maxRetryCnt = 10) {
		let self = this;

		if (!paymentTextSelector || !title || self.getPriceCents() < 1) {
			return;
		}

		let elem = document.querySelector(paymentTextSelector);
		if (elem) {
			self.debug("CheckoutPage, paymentTextSelector found, changing text to: %s, price is: %d", title, self.getPriceCents());
			elem.innerHTML = title;

			if (retryCnt > 0) {
				Utils.delay(1000).then(() => {
					// Check if content is attached
					self.elementBinding.hardRefreshElement("sparxpresContent", true, true);
					if (!self.elementBinding.getElement("sparxpresContent")) {
						self.debug("CheckoutPage.setPaymentTextDescription, content not attached, so attach...")
						self.attachContent(self.loadedContent.style, self.loadedContent.html)
							.then(result => {
								if (result) self.contentAttached();
							});
					} else {
						self.debug("CheckoutPage.setPaymentTextDescription, content already attached")
					}
				})
			}
		} else {
			if (retryCnt < maxRetryCnt) {
				const delaySeconds = retryCnt > 4 ? 2 : 1;
				self.debug("CheckoutPage, paymentTextSelector not found, wait %d seconds before retrying, retry %d of %d", delaySeconds, retryCnt+1, maxRetryCnt);
				Utils.delay(delaySeconds*1000).then(() => {
					self.setPaymentTextDescription(paymentTextSelector, title, retryCnt+1, maxRetryCnt);
				});
			} else {
				self.debug("CheckoutPage, paymentTextSelector not found.");
			}
		}
	}


	/**
	 * Override PageProcessor getContent
	 * @return {Promise}
	 */
	getContent()
	{
		let self = this;

		let _priceCents = self.getPriceCents() || self.collectedDataLoader.get("price") || self.collectedDataLoader.get("cartPrice");
		return self.getLoanInformation(_priceCents, self.duration)
			.then(loanInfo => {
				const showFinance = Utils.isFinanceEnabled(loanInfo, _priceCents);
				const showXpresPay = Utils.isXpresPayEnabled(loanInfo, _priceCents);

				if (showFinance && showXpresPay) {
					return self.contentService.getContent("checkout", "both").then(data => data)
				} else if (showFinance) {
					return self.contentService.getContent("checkout", "finance").then(data => data)
				} else if (showXpresPay) {
					return self.contentService.getContent("checkout", "xprespay").then(data => data)
				} else {
					return self.contentService.getContent("checkout", "alert").then(data => data)
				}
			})
			.catch(error => {
				Utils.info("Failed to get content: %s", error);
			});
	}

	/**
	 *
	 * @return {string}
	 */
	getLoanApplicationUrl() {
		if (!this.linkId) {
			throw new Error("No link id found!");
		}

		const urlParameters = new URLSearchParams({
			"linkId": this.linkId,
			"amountCents": this.getPriceCents(true) || this.collectedDataLoader.get("price") || this.collectedDataLoader.get("cartPrice") || '',
			"transactionId": this.collectedDataLoader.get("reference") || '',
			"period": Session.get('sparxpres-default-period') || '',
			"email": this.collectedDataLoader.get("email") || '',
			"phone": this.collectedDataLoader.get("phone") || '',
			"accepturl": "post-message"
		});

		return config("externalPages.checkout") + "?" + urlParameters.toString();
	}

	bindElements(settings)
	{
		this.elementBinding.addElement("sparxpresInnerContent", "." + this.getWrapperClass() + " .sparxpres-container", true);
		this.elementBinding.addElement("insertContainer", this.settings.display.insertContainer, true);
		this.elementBinding.addElement("sparxpresContent", "." + this.getWrapperClass(), true);
		this.elementBinding.addElement("checkoutSubmitButton", this.settings.scraping.fields.checkoutSubmitButton, true);
//		this.elementBinding.addElement("nb", "." + this.getWrapperClass() + " .Sparxpres__NB");
//		this.elementBinding.addElement("priceOutOfRange", ".Sparxpres__price-out-of-range", true);

		let paymentMethodAttribute = this.settings.display.paymentMethodAttribute;
		this.elementBinding.addElement(
			"paymentMethod",
			this.settings.scraping.fields.paymentMethod,
			false,
			paymentMethodAttribute ? paymentMethodAttribute : ''
		);

		let priceAttribute = this.settings.display.priceAttribute;
		this.elementBinding.addElement(
			"price",
			this.settings.scraping.fields.price,
			false,
			priceAttribute ? priceAttribute : ''
		);

		// form selector is used to prevent form from submitting if user press enter
		if (this.settings.scraping.fields.form && this.settings.scraping.fields.form.length > 0) {
			this.elementBinding.addElement("checkoutForm", this.settings.scraping.fields.form);

			let self = this;
			this.elementBinding.addEventListenerToElement("checkoutForm", "submit", function(event) {
				let paymentMethod = self.collectedDataLoader.get("paymentMethod");

				if (paymentMethod === self.settings.display.paymentMethodValue) {
					let successStatuses = ["SIGNED", "NOTSIGNED", "REFERRER", "XPRESPAY_CAPTURED"];
					let status = Session.get(self.sessionKeys.status);
					if (successStatuses.indexOf(status) === -1) {
						event.preventDefault();
					}
				}
			});
		}

		let fieldsClone = Object.assign({}, this.settings.scraping.fields);
		delete fieldsClone.price;
		delete fieldsClone.paymentMethod;
		delete fieldsClone.sparxpresContent;
		delete fieldsClone.checkoutSubmitButton;
		delete fieldsClone.checkoutSubmitButtonClick;

		this.elementBinding.addElements(fieldsClone);
	}

	addElementRefreshEvents(settings) {
		let self = this;

		const useCapture = self.settings.general.hasOwnProperty('useCaptureForCheckoutEventListener') && self.settings.general.useCaptureForCheckoutEventListener === "1";
		self.elementBinding.addRefreshEvent('change', true, () => {
			self.debug("CheckoutPage change event caught");
			if (self.active && self.loadedContent) {
				if (!self.onProductPage()) {
					self.elementBinding.hardRefreshElement("sparxpresContent", true, true);
					if (!self.elementBinding.getElement("sparxpresContent")) {
						self.attachContent(self.loadedContent.style, self.loadedContent.html)
							.then(result => { if (result) self.contentAttached(); });

					} else {
						self.updateVisibility().then();
					}
				} else {
					self.attachContent(self.loadedContent.style, self.loadedContent.html)
						.then(result => { if (result) self.contentAttached(); });
				}
			}
		}, useCapture);

		if (!self.onProductPage()) {
			const observerSelector = self.settings.display.observerSelector;
			if (observerSelector) {
				self.debug("CheckoutPage ObserverSelector: %s", observerSelector);
				const mutationContainer = document.querySelector(observerSelector);
				self.debug("CheckoutPage MutationContainer: %s", mutationContainer);
				if (mutationContainer) {
					const mutationConfig = {
						attributes: false,
						childList: true,
						subtree: false,
						characterData: false,
						characterDataOldValue: false
					};

					const domObserver = new MutationObserver(function (mutations) {
						if (!self.active || !self.loadedContent) return;

						self.debug('CheckoutPage MutationObserver function called: %s', mutations);
						if (self.isInDebugMode) console.table(mutations);

						self.elementBinding.hardRefreshElement("sparxpresContent", true, true);
						if (!self.elementBinding.getElement("sparxpresContent")) {
							self.attachContent(self.loadedContent.style, self.loadedContent.html)
								.then(result => { if (result) self.contentAttached(); });
						} else {
							self.updateVisibility().then();
						}
					});
					domObserver.observe(mutationContainer, mutationConfig);
				}
			}
		}
	}

	/**
	 * Update visibility
	 * @return {Promise}
	 */
	updateVisibility()
	{
		let self = this;

		return self.readyForAttach()
			.then(result => {
				if (result) {
					self.elementBinding.show("sparxpresContent");

					if (!Session.get(self.sessionKeys.status)) {
						self.hideCheckoutSubmitButtonDisplay();
					}
					return true;
				} else {
					self.elementBinding.removeFromDom("sparxpresContent");
					self.showCheckoutSubmitButtonDisplay();

					return false;
				}
			});
	}

	/**
	 * Add post message event listener
	 */
	addPostMessageEventListener()
	{
		let self = this;

		// Listen for messages coming from iframe
		window.addEventListener("message", (event) => {
			if (!event.origin.match(/sparxpres\.dk/)) {
				return;
			}

			if (event.data.status) {
				self.updateViewWithResult(event.data.status, true);

				// hide/remove application page
				const applicationModal = document.getElementById("sparxpresLoanApplicationPageModal");
				if (applicationModal) {
					applicationModal.remove();
				}
			}
		}, false);
	}

	/**
	 * Update view with result
	 * @param status
	 * @param triggeredFromPostMessage
	 */
	updateViewWithResult(status, triggeredFromPostMessage = false)
	{
		const area = !this.onProductPage() ? "checkout" : "product-checkout";
		const successStatuses = ["SIGNED", "NOTSIGNED", "REFERRER", "XPRESPAY_CAPTURED"];
		Session.set(this.sessionKeys.status, status);

		if (successStatuses.indexOf(status) !== -1) {
			this.showCheckoutSubmitButtonDisplay(triggeredFromPostMessage);
		}

		this.elementBinding.hardRefreshElement('sparxpresInnerContent');
		this.elementBinding.setValue(
			'sparxpresInnerContent',
			`
				<div class="sparxpres-alert">
					<div class="sparxpres-alert-cell">
						${textObject[area].loanApplication[status].svgIcon}
					</div>
					<div class="sparxpres-alert-cell">
						<div class="sparxpres-paragraph">${textObject[area].loanApplication[status].message}</div>
					</div>
				</div>
			`
		);
	}

	/**
	 * Check if needed data is available
	 * @return {Promise}
	 */
	readyForAttach()
	{
		let self = this;

		const price = self.getPriceCents() || self.collectedDataLoader.get("price") || self.collectedDataLoader.get("cartPrice");
		if (self.onProductPage()) {
			self.elementBinding.hardRefreshElement('price');
			self.elementBinding.hardRefreshElement('insertContainer');

			return Promise.resolve(price>0 && self.elementBinding.getElement('insertContainer'));
		} else {
			self.elementBinding.hardRefreshElement('price');
			self.elementBinding.hardRefreshElement('checkoutSubmitButton', true);
			self.elementBinding.hardRefreshElement('sparxpresContent', true);
			self.elementBinding.hardRefreshElement("insertContainer", true, true);
			self.elementBinding.hardRefreshElement('paymentMethod', true);

			// self.elementBinding.hardRefreshElement('paymentMethodGeneral', true, true);
			// if (self.settings.scraping.fields.paymentMethodGeneral &&
			// 	self.settings.scraping.fields.paymentMethodGeneral.length) {
			// 	// Return false early in case we can get: paymentMethodGeneral but we don't have a value in: paymentMethod
			// 	if ((self.elementBinding.getElement('paymentMethodGeneral') && !self.elementBinding.getElement('paymentMethod'))
			// 		|| !self.elementBinding.getElement('paymentMethodGeneral')
			// 	) {
			// 		return Promise.resolve(false);
			// 	}
			// }

			let insertContainer = self.elementBinding.getElement("insertContainer");
			let paymentMethod = self.collectedDataLoader.get("paymentMethod");

			if (paymentMethod === this.settings.display.paymentMethodValue
				&& price>0
				&& insertContainer
				&& this.elementBinding.getElement("checkoutSubmitButton")
			) {
				return Promise.resolve(true);
			} else {
				self.debug("Could not show XpresWebSale checkout! (paymentMethodValue=%s (was: %s) | price=%s | insertContainer=%s | checkoutButton=%s)",
					paymentMethod === this.settings.display.paymentMethodValue,
					paymentMethod,
					price > 0,
					insertContainer != null,
					this.elementBinding.getElement("checkoutSubmitButton") != null
				);

				return Promise.resolve(false);
			}
		}
	}

	/**
	 * Rewrite of PageProcessor.attachHTMLToView
	 * @param {String} insertPosition
	 * @param {String} html
	 * @param {String} style
	 * @return {Promise}
	 */
	attachHTMLToView(insertPosition, html = '', style = '')
	{
		let self = this;

		return self.readyForAttach()
			.then(result => {
				if (!result) {
					self.showCheckoutSubmitButtonDisplay();	// make sure checkout button is visible
					return false;	// Not all required elements have been found.
				}

				try {
					self.hideCheckoutSubmitButtonDisplay();
					self.elementBinding.addToElement(
						'insertContainer',
						insertPosition,
						self.getWrapperClass(),
						self.getWrapperClassStyle(),
						html,
						style
					);

					self.elementBinding.hardRefreshElement("sparxpresContent");
					self.elementBinding.show("sparxpresContent");

					return true;
				} catch (error) {
					console.log("Insert error: ", error);
					self.elementBinding.hide("sparxpresContent");
				}
				return false;
			});
	}

	onProductPage()
	{
		return !!+this.settings.display.onProductPage;
	}

	/**
	 * Process content from remote services (html, css and js)
	 * @param style
	 * @param content
	 * @return {Promise}
	 */
	attachContent(style = '', content = '')
	{
		let self = this;

		if (self.onProductPage()) {
			self.elementBinding.hardRefreshElement("sparxpresContent");
		} else {
			self.elementBinding.hardRefreshElement("sparxpresContent", true, true);
		}

		if (self.elementBinding.getElement("sparxpresContent")) {
			return Promise.reject();
		}

		return self.attachHTMLToView(
			self.settings.display.insertMethod,
			content,
			style
		);
	}

	/**
	 * Called after content is attached
	 * @param script
	 */
	contentAttached(script = '') {
		let self = this;

		// In case the customer already applied for the loan
		if (Session.get(self.sessionKeys.status)) {
			self.updateViewWithResult(Session.get(self.sessionKeys.status));
			return;
		}

		self.afterAttachment(script);
	}

	/**
	 *
	 * @param triggeredFromPostMessage
	 */
	showCheckoutSubmitButtonDisplay(triggeredFromPostMessage = false) {
		let self = this;

		if (self.settings.display.checkoutSubmitButtonDisplay) {
			self.elementBinding.show("checkoutSubmitButton", self.settings.display.checkoutSubmitButtonDisplay);
		} else {
			self.elementBinding.show("checkoutSubmitButton");
		}

		if (triggeredFromPostMessage) {
			setTimeout(() => {
				self.tryToSubmitForm(self.settings.scraping.fields.checkoutSubmitButtonClick);
			}, 1000);
		}
	}

	/**
	 *
	 */
	hideCheckoutSubmitButtonDisplay() {
		let self = this;

		if (self.settings.display.checkoutSubmitButtonDisplayNone) {
			self.elementBinding.hide("checkoutSubmitButton", self.settings.display.checkoutSubmitButtonDisplayNone);
		} else {
			self.elementBinding.hide("checkoutSubmitButton");
		}
	}

	/**
	 * Try to click on checkout submit button
	 */
	tryToSubmitForm(clickButtons = [])
	{
		if (clickButtons && clickButtons.length > 0) {
			this.debug("clickButtons length: %d", clickButtons.length);

			clickButtons.every(itm => {
				const clickBtnElem = document.querySelector(itm);
				if (clickBtnElem) {
					this.debug("clickButtons element (%s) exist, trying to click on it....", itm);
					clickBtnElem.click();
					return false;		// break out of loop
				} else {
					this.debug("clickButtons element (%s) does not exists...", itm);
					return true;		// continue in loop
				}
			});
		}
	}

}
